import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectTrigger,
  SelectContent,
  SelectItem,
  SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';
import {
  IconEdit,
  IconTrash,
  IconArrowUp,
  IconArrowDown,
  IconRoute,
  IconGripVertical,
} from '@tabler/icons-react';
import type { CardQuestion, SurveyPath, SurveyQuestionType } from '../../types';
import { normalizeTriggerValues } from '../../utils/pathTriggerValues';
import { getPathColor } from '../../utils/pathColors';

interface QuestionBlockProps {
  question: CardQuestion;
  path?: SurveyPath;
  onUpdate: (updates: Partial<CardQuestion>) => void;
  onDelete: () => void;
  onDuplicate?: () => void;
  onMoveUp?: () => void;
  onMoveDown?: () => void;
  onCreatePath?: (optionValue?: string) => void;
  onEditPath?: () => void;
  onEditPathForOption?: (optionValue: string) => void;
  onRemovePathFromOption?: (optionValue: string) => void;
  onOptionRemove?: (optionValue: string) => void;
  canMoveUp: boolean;
  canMoveDown: boolean;
  triggerPaths?: SurveyPath[];
  onDragStartForPath?: (e: React.DragEvent) => void;
  onDragEndForPath?: () => void;
}

export function QuestionBlock({
  question,
  path,
  onUpdate,
  onDelete,
  onDuplicate,
  onMoveUp,
  onMoveDown,
  onCreatePath,
  onEditPath,
  onEditPathForOption,
  onRemovePathFromOption,
  onOptionRemove,
  canMoveUp,
  canMoveDown,
  triggerPaths = [],
  onDragStartForPath,
  onDragEndForPath,
}: QuestionBlockProps) {
  const [isEditing, setIsEditing] = useState(!question.text);
  const [isFocused, setIsFocused] = useState(false);

  const handleTextChange = (text: string) => {
    onUpdate({ text });
    if (text.trim() && !isEditing) {
      setIsEditing(true);
    }
  };

  const handleTextFieldBlur = (e: React.FocusEvent) => {
    setTimeout(() => {
      const activeElement = document.activeElement;
      if (activeElement && (
        activeElement.tagName === 'BUTTON' ||
        activeElement.closest('button') ||
        activeElement.closest('[role="menuitem"]')
      )) {
        return;
      }
      if (question.text.trim()) {
        setIsEditing(false);
      }
      setIsFocused(false);
    }, 150);
  };

  const handleTextFieldFocus = () => {
    setIsFocused(true);
    setIsEditing(true);
  };

  const handleRequiredChange = (required: boolean) => {
    onUpdate({ required });
  };

  const handleOptionAdd = (option: string) => {
    if (!question.config.options) return;
    onUpdate({
      config: {
        ...question.config,
        options: [...question.config.options, option],
      },
    });
  };

  const handleOptionRemove = (option: string) => {
    if (onOptionRemove) {
      onOptionRemove(option);
      return;
    }
    if (!question.config.options) return;
    onUpdate({
      config: {
        ...question.config,
        options: question.config.options.filter((o) => o !== option),
      },
    });
  };

  const handleOptionChange = (index: number, newValue: string) => {
    if (!question.config.options) return;
    const updated = [...question.config.options];
    updated[index] = newValue;
    onUpdate({
      config: {
        ...question.config,
        options: updated,
      },
    });
  };

  const handleRatingFormatChange = (newFormat: 'numerical' | 'satisfaction') => {
    onUpdate({
      config: {
        ...question.config,
        format: newFormat,
        rateAmount: newFormat === 'satisfaction' ? 5 : question.config.rateAmount ?? 5,
      },
    });
  };

  const handleRatingScaleChange = (rateAmount: number) => {
    onUpdate({
      config: {
        ...question.config,
        format: question.config.format ?? 'numerical',
        rateAmount,
      },
    });
  };

  const renderQuestionContent = () => {
    switch (question.type) {
      case 'rating': {
        const ratingFormat = question.config.format ?? 'numerical';
        const rateAmount = question.config.rateAmount ?? 5;
        return (
          <div className="mt-2">
            <p className="mb-1 text-sm text-muted-foreground">Rating Scale</p>
            <div className="flex flex-wrap items-center gap-2">
              <div className="inline-flex rounded-md border">
                <button
                  type="button"
                  onClick={() => handleRatingFormatChange('numerical')}
                  className={cn(
                    'rounded-l-md px-3 py-1 text-sm',
                    ratingFormat === 'numerical' ? 'bg-primary text-primary-foreground' : 'bg-background hover:bg-muted'
                  )}
                >
                  Numerical
                </button>
                <button
                  type="button"
                  onClick={() => handleRatingFormatChange('satisfaction')}
                  className={cn(
                    'rounded-r-md border-l px-3 py-1 text-sm',
                    ratingFormat === 'satisfaction' ? 'bg-primary text-primary-foreground' : 'bg-background hover:bg-muted'
                  )}
                >
                  Satisfaction
                </button>
              </div>
              {ratingFormat === 'satisfaction' ? (
                <Badge variant="secondary">1–5</Badge>
              ) : (
                <Select
                  value={String(rateAmount)}
                  onValueChange={(v) => handleRatingScaleChange(Number(v))}
                >
                  <SelectTrigger className="w-24">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {[3, 4, 5, 6, 7, 8, 9, 10].map((n) => (
                      <SelectItem key={n} value={String(n)}>
                        1–{n}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>
          </div>
        );
      }

      case 'radio':
      case 'checkbox':
        return (
          <div className="mt-2">
            <p className="mb-1 text-sm text-muted-foreground">Options</p>
            {question.config.options && question.config.options.length > 0 ? (
              <div className="space-y-1">
                {question.config.options.map((option, index) => (
                  <div key={index} className="flex flex-wrap items-center gap-1">
                    {question.type === 'radio' ? (
                      <input type="radio" disabled className="size-4" />
                    ) : (
                      <input type="checkbox" disabled className="size-4" />
                    )}
                    <Input
                      value={option}
                      onChange={(e) => handleOptionChange(index, e.target.value)}
                      placeholder="Option text"
                      className="min-w-[120px] flex-1"
                    />
                    {question.type === 'radio' && (() => {
                      const optionVal = (option ?? '').trim();
                      const optionsList = question.config?.options ?? [];
                      const currentOptionValues = new Set(
                        optionsList.map((o) => (o != null ? String(o).trim() : ''))
                      );
                      const pathForOption = optionVal
                        ? triggerPaths.find((p) => {
                            if (p.trigger.questionId !== question.id) return false;
                            return normalizeTriggerValues(p.trigger.values).includes(optionVal);
                          })
                        : null;
                      const emptyCount = optionsList.filter((o) => !(o != null ? String(o).trim() : '')).length;
                      const orphanedPath =
                        !optionVal && emptyCount === 1
                          ? triggerPaths.find((p) => {
                              if (p.trigger.questionId !== question.id) return false;
                              const vals = normalizeTriggerValues(p.trigger.values);
                              return vals.some((v) => v !== '' && !currentOptionValues.has(v));
                            })
                          : null;
                      const pathToShow = pathForOption ?? orphanedPath;
                      const valueForPathCallbacks = pathForOption
                        ? optionVal
                        : orphanedPath
                          ? (normalizeTriggerValues(orphanedPath.trigger.values).find((v) => v !== '' && !currentOptionValues.has(v)) ?? '')
                          : '';
                      if (pathToShow && valueForPathCallbacks && (onEditPathForOption || onRemovePathFromOption)) {
                        return (
                          <div className="flex flex-shrink-0 items-center gap-0.5">
                            {onEditPathForOption && (
                              <Button
                                variant="link"
                                size="sm"
                                onClick={() => onEditPathForOption(valueForPathCallbacks)}
                                title={pathToShow.name ? `Edit path: ${pathToShow.name}` : 'Edit path'}
                              >
                                <IconEdit className="size-3" />
                                Edit path{pathToShow.name ? ` (${pathToShow.name})` : ''}
                              </Button>
                            )}
                            {onRemovePathFromOption && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => onRemovePathFromOption(valueForPathCallbacks)}
                                title="Remove path from this option (option stays)"
                              >
                                Remove path
                              </Button>
                            )}
                          </div>
                        );
                      }
                      if (!pathToShow && onCreatePath && optionVal) {
                        return (
                          <Button
                            variant="link"
                            size="sm"
                            onClick={() => onCreatePath(optionVal)}
                            className="flex-shrink-0"
                          >
                            <IconRoute className="size-3" />
                            Create path
                          </Button>
                        );
                      }
                      return null;
                    })()}
                    <Button
                      variant="ghost"
                      size="icon-xs"
                      onClick={() => handleOptionRemove(option)}
                      className="text-destructive"
                    >
                      <IconTrash className="size-3.5" />
                    </Button>
                  </div>
                ))}
              </div>
            ) : null}
            <Button
              variant="outline"
              size="sm"
              onClick={() => handleOptionAdd('')}
              className="mt-1"
            >
              + Add Option
            </Button>
          </div>
        );

      case 'textfield':
        return (
          <div className="mt-2">
            <p className="text-sm text-muted-foreground">Text Response</p>
          </div>
        );

      case 'done':
        return (
          <div className="mt-2 py-1">
            <p className="text-sm font-medium text-primary">
              Survey end — reward allocation runs when respondent reaches this step
            </p>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <Card
      data-question-id={question.id}
      className={cn(
        'relative mb-2 p-3',
        path ? 'border-2 border-primary' : 'border'
      )}
    >
      {/* Header */}
      <div className="mb-2 flex items-start justify-between">
        <div className="flex min-w-0 flex-1 items-start gap-0.5">
          {onDragStartForPath && question.type !== 'done' && (
            <div
              draggable
              onDragStart={onDragStartForPath}
              onDragEnd={onDragEndForPath}
              className="flex cursor-grab items-center pt-0.5 text-muted-foreground active:cursor-grabbing"
              title={path ? 'Drag to reorder or move out of branch' : 'Drag to move into a branch'}
            >
              <IconGripVertical className="size-4" />
            </div>
          )}
          <div className="min-w-0 flex-1">
          <div className="mb-1 flex flex-wrap items-center gap-1">
            <span className="text-lg font-semibold text-primary">
              {question.number}
            </span>
            {path && (() => {
              const pathColor = getPathColor(path.id);
              return (
                <Badge
                  className="cursor-pointer font-bold text-white"
                  style={{ backgroundColor: pathColor.main }}
                  onClick={(e) => {
                    e.stopPropagation();
                    onEditPath?.();
                  }}
                >
                  <IconRoute className="mr-1 size-3" />
                  {path.name}
                </Badge>
              );
            })()}
            {triggerPaths.length > 0 && triggerPaths.map((triggerPath) => {
              const pathColor = getPathColor(triggerPath.id);
              return (
                <Badge
                  key={triggerPath.id}
                  variant="outline"
                  className="cursor-pointer font-medium hover:text-white"
                  style={{
                    backgroundColor: pathColor.light,
                    color: pathColor.main,
                    borderColor: pathColor.main,
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    onEditPath?.();
                  }}
                >
                  <IconRoute className="mr-1 size-3" />
                  {triggerPath.name} ({triggerPath.questionIds.length}Q)
                </Badge>
              );
            })}
          </div>

          {question.type === 'done' ? (
            <h6 className="text-lg font-medium text-primary">
              Survey end
            </h6>
          ) : isEditing ? (
            <Input
              value={question.text}
              onChange={(e) => handleTextChange(e.target.value)}
              onFocus={handleTextFieldFocus}
              onBlur={handleTextFieldBlur}
              placeholder="Enter your question"
              autoFocus={!question.text}
              onKeyDown={(e) => {
                if (e.key === 'Escape') {
                  setIsEditing(false);
                }
              }}
              className="border-0 border-b border-border bg-transparent px-0 text-lg font-semibold shadow-none focus-visible:ring-0"
            />
          ) : (
            <h6
              onClick={() => setIsEditing(true)}
              className="min-h-[32px] cursor-pointer text-lg font-semibold hover:underline"
            >
              {question.text || 'Click to add question text'}
            </h6>
          )}
          </div>
        </div>

        {question.type !== 'done' && (
          <Button
            variant="ghost"
            size="icon-xs"
            onClick={(e) => {
              e.stopPropagation();
              onDelete();
            }}
            className="shrink-0 text-destructive"
            aria-label="Delete question"
            title="Delete question"
          >
            <IconTrash className="size-4" />
          </Button>
        )}
      </div>

      {renderQuestionContent()}

      {/* Footer */}
      <div className="mt-2 flex items-center justify-between border-t pt-2">
        <div className="flex items-center gap-2">
          <Switch
            checked={question.required}
            onCheckedChange={handleRequiredChange}
          />
          <Label className="text-sm">Required</Label>
        </div>

        <div className="flex gap-1">
          {question.type !== 'done' && onMoveUp && (
            <Button variant="ghost" size="icon-xs" onClick={onMoveUp} disabled={!canMoveUp}>
              <IconArrowUp className="size-4" />
            </Button>
          )}
          {question.type !== 'done' && onMoveDown && (
            <Button variant="ghost" size="icon-xs" onClick={onMoveDown} disabled={!canMoveDown}>
              <IconArrowDown className="size-4" />
            </Button>
          )}
          {!path && onCreatePath && question.type === 'radio' && (
            <Button variant="ghost" size="icon-xs" onClick={() => onCreatePath()} title="Create path (choose trigger in wizard)">
              <IconRoute className="size-4" />
            </Button>
          )}
          {path && onEditPath && (
            <Button variant="ghost" size="icon-xs" onClick={onEditPath}>
              <IconEdit className="size-4" />
            </Button>
          )}
        </div>
      </div>
    </Card>
  );
}
